import { DocumentOrganizationKeys } from '@griply/types';
import {
  groupByModeTranslationKeys,
  sortingModeTranslationKeys,
} from '@griply/utils';
import { TFunction } from 'i18next';
import { Layers, Sort } from 'shared/assets/icons';
import {
  FilterGroup,
  FilterOption,
  FilterOptionType,
} from 'shared/types/filter';
import { GroupByMode, SortingMode } from 'shared/types/sorting-mode';

export type Options = {
  t: TFunction;
  supportedGroupByModes?: readonly GroupByMode[];
  supportedSortingModes?: readonly SortingMode[];
  labelOverrides?: Partial<Record<GroupByMode, string>>;
};

export const createGroupingAndSorting = ({
  t,
  supportedSortingModes,
  supportedGroupByModes,
  labelOverrides,
}: Options): FilterGroup[] => {
  const sortOptions = [
    SortingMode.Custom,
    SortingMode.DueDate,
    SortingMode.Priority,
    SortingMode.DeadlineDeadline,
    SortingMode.Name,
    SortingMode.CreatedAt,
  ].map((value) => ({
    label: t(sortingModeTranslationKeys[value] ?? ''),
    value,
  }));

  const groupByOptions = (
    [
      GroupByMode.None,
      GroupByMode.Section,
      GroupByMode.LifeArea,
      GroupByMode.Goal,
      GroupByMode.Tag,
      GroupByMode.Deadline,
      GroupByMode.Priority,
      GroupByMode.DeadlineDeadline,
      GroupByMode.DayPart,
      GroupByMode.Type,
    ] as const
  ).map((value) => ({
    label:
      labelOverrides?.[value] ?? t(groupByModeTranslationKeys[value] ?? ''),
    value,
  }));

  const filteredSortOptions =
    !supportedSortingModes || supportedSortingModes.length
      ? supportedSortingModes?.length
        ? supportedSortingModes
            .map((mode) => sortOptions.find((option) => option.value === mode))
            .filter((option): option is (typeof sortOptions)[0] => !!option)
        : sortOptions
      : undefined;

  const filteredGroupByOptions =
    !supportedGroupByModes || supportedGroupByModes.length
      ? supportedGroupByModes?.length
        ? supportedGroupByModes
            .map((mode) =>
              groupByOptions.find((option) => option.value === mode),
            )
            .filter((option): option is (typeof groupByOptions)[0] => !!option)
        : groupByOptions
      : undefined;

  const filters: FilterOption[] = [];
  if (filteredSortOptions) {
    // Sorting
    filters.push({
      id: DocumentOrganizationKeys.SortingMode,
      label: t('context-menus.filtering.task.grouping-and-sorting.sort.label'),
      placeholder: t('general.labels.select'),
      icon: Sort,
      type: FilterOptionType.Select,
      selectOptions: filteredSortOptions,
    });
  }

  if (filteredGroupByOptions) {
    // Grouping
    filters.push({
      id: DocumentOrganizationKeys.GroupByMode,
      label: t(
        'context-menus.filtering.task.grouping-and-sorting.grouping.label',
      ),
      placeholder: t('general.labels.select'),
      icon: Layers,
      type: FilterOptionType.Select,
      selectOptions: filteredGroupByOptions,
    });
  }

  if (filters.length) {
    return [
      {
        id: 'groupingAndSorting',
        title: t('context-menus.filtering.task.grouping-and-sorting.title'),
        filters,
      },
    ];
  }

  return [];
};
